/**
 * RISKS-RISK.ROUTE
 * Dedicated route for editing a single risk
 * Pattern mirrors /risks/unit/:id
 */
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { fetchValidatedAndSet } from "@/features/risks/entities/risk/model/risk.thunk"
import NavRisks from "@/features/risks/ui/nav-risks"
import RiskEditCardOpen from "@/features/risks/entities/risk/ui/RiskEditCardOpen"
import RiskSuccessModal from "@/features/risks/entities/risk/ui/modals/RiskSuccessModal"

export default function RisksRiskRoute() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { uuid } = useParams<{ uuid: string }>()

  const active = useAppSelector(riskStore.selectors.active)
  const risks = useAppSelector(riskStore.selectors.list)
  const units = useAppSelector((state: any) => state.unit?.list || [])

  // Track backTo in state to ensure updates when location changes
  const [backTo, setBackTo] = useState<string | undefined>()
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  // Update backTo when location state or uuid changes
  useEffect(() => {
    const newBackTo = (location.state as any)?.backTo
    const shouldShowSuccess = (location.state as any)?.showSuccessModal
    setBackTo(newBackTo)
    setShowSuccessModal(shouldShowSuccess || false)
  }, [location.state, uuid])

  // Build back text based on where we came from (memoized to recalculate when backTo changes)
  const backText = useMemo(() => {
    if (backTo === "/risks/units") {
      return "Unités de travail"
    }
    if (backTo?.includes("/unit/")) {
      const unitId = backTo.split("/").pop()
      const unit = units.find((u: any) => u.id === unitId)
      if (unit) {
        return unit.name
      }
    }
    return "Fiches de risques"
  }, [backTo, units])

  useEffect(() => {
    dispatch(fetchValidatedAndSet(null))
  }, [])

  useEffect(() => {
    if (uuid && (!active || active.id !== uuid)) {
      const found = risks.find((r) => r.id === uuid)
      if (found) {
        const unitById = new Map<string, any>(units.map((u: any) => [u.id, u]))
        const cleanName = typeof found.name === "string" ? found.name.trim() : found.name
        const siblings = risks.filter(
          (r) => r.id !== found.id && (typeof r.name === "string" ? r.name.trim() : r.name) === cleanName
        )
        const enriched = {
          ...found,
          unitSize: unitById.get(found.UnitId ?? "")?.size ?? null,
          associatedRisks: siblings.map((ar: any) => ({
            ...ar,
            unitName: unitById.get(ar.UnitId ?? "")?.name ?? ar.unitName ?? null,
            unitSize: unitById.get(ar.UnitId ?? "")?.size ?? null
          }))
        }
        dispatch(riskStore.actions.setActive(enriched))
      }
    }
  }, [uuid, dispatch, risks, active, units])

  // ✅ Étape 8 complete: dateValidated = now is set in RiskEditCardOpen.performSave() when isModal=true
  // This makes the risk "validated" on save (simplifies workflow, skips submitted state)
  // History preserved via sourceId + RiskHistorySideNav

  if (!active || active.id !== uuid) {
    return null
  }

  return (
    <>
      {showSuccessModal && <RiskSuccessModal onClose={() => setShowSuccessModal(false)} />}
      <NavRisks
        text={backText}
        onBack={() => {
          if (backTo) {
            // Return to the origin page (/risks or /risks/unit/:uuid)
            navigate(backTo)
          } else {
            // Otherwise use browser back
            navigate(-1)
          }
        }}>
        <div className="p-8">
          <RiskEditCardOpen isModal />
        </div>
      </NavRisks>
    </>
  )
}
